import {api} from './api.js';
import type {AuthSnapshot} from './auth.js';
import {apiUrl} from './config.js';
import {escapeHtml} from './layout.js';

type WorkJob = {
  id: string;
  family: 'recognition' | 'legal-review' | 'workflow';
  type: string;
  typeLabel: string;
  business: 'finance' | 'legal';
  organizationId: string;
  taskId: string;
  taskName: string;
  objectName: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  stuck: boolean;
  progress: number;
  phase: string;
  durationMs: number;
  createdAt: string;
  createdBy: string;
  createdByName: string;
  error: string;
  attachmentCount: number;
  visionPhase: boolean;
  canRetry: boolean;
  canTimeout: boolean;
};

type JobsPayload = {
  jobs: WorkJob[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: {running: number; stuck: number; failed24h: number; completedToday: number; failed: number};
  users: Array<{id: string; name: string; email: string; role: string}>;
};

type JobDetail = WorkJob & {
  aiCalls?: Array<{id: string; operation: string; status: string; durationMs: number; error: string; createdAt: string}>;
};

/**
 * 将毫秒耗时格式化为中文时长。
 * @param value 毫秒
 */
function formatDuration(value: number): string {
  const ms = Math.max(0, Number(value) || 0);
  if (ms < 60_000) return `${Math.round(ms / 1000)} 秒`;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} 分`;
  return `${Math.floor(minutes / 60)} 时 ${minutes % 60} 分`;
}

/**
 * 作业状态标签。
 * @param job 作业行
 */
function statusTag(job: WorkJob): string {
  if (job.stuck) return '<span class="status-tag warning">卡住</span>';
  if (job.status === 'failed') return '<span class="status-tag danger">失败</span>';
  if (job.status === 'completed') return '<span class="status-tag success">完成</span>';
  if (job.status === 'queued') return '<span class="status-tag">排队</span>';
  return '<span class="status-tag info">进行中</span>';
}

/**
 * 后台作业监控页：跨财务识别、法务审核与申报归档。
 * @param host 路由容器
 * @param auth 当前管理员
 */
export async function jobsPage(host: HTMLElement, auth: AuthSnapshot): Promise<void> {
  const canTimeout = auth.role === 'super_admin';
  const expanded = new Set<string>();
  const selected = new Map<string, WorkJob['family']>();
  let refreshTimer = 0;
  const date = (value: unknown) => value ? new Date(String(value)).toLocaleString('zh-CN') : '—';

  const collectParams = () => {
    const params = new URLSearchParams();
    const page = Number(host.querySelector<HTMLInputElement>('#jobsPage')?.value || 1);
    const view = host.querySelector<HTMLSelectElement>('#jobsView')?.value || 'attention';
    const business = host.querySelector<HTMLSelectElement>('#jobsBusiness')?.value || '';
    const family = host.querySelector<HTMLSelectElement>('#jobsFamily')?.value || '';
    const status = host.querySelector<HTMLSelectElement>('#jobsStatus')?.value || '';
    const userId = host.querySelector<HTMLSelectElement>('#jobsUser')?.value || '';
    const from = host.querySelector<HTMLInputElement>('#jobsFrom')?.value || '';
    const to = host.querySelector<HTMLInputElement>('#jobsTo')?.value || '';
    params.set('page', String(page));
    params.set('pageSize', '20');
    params.set('view', view);
    if (business) params.set('business', business);
    if (family) params.set('family', family);
    if (status) params.set('status', status);
    if (userId) params.set('userId', userId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return params;
  };

  const fillUserSelect = (select: HTMLSelectElement | null, items: Array<{id: string; name: string}>) => {
    if (!select) return;
    const current = select.value;
    select.innerHTML = `<option value="">全部操作用户</option>${items.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.name)}</option>`).join('')}`;
    if ([...select.options].some((option) => option.value === current)) select.value = current;
  };

  const detailHtml = (job: WorkJob, detail?: JobDetail) => {
    const calls = detail?.aiCalls ?? [];
    const callRows = calls.length
      ? calls.map((item) => `<li>${escapeHtml(item.operation)} · ${escapeHtml(item.status)} · ${item.durationMs} ms${item.error ? ` · ${escapeHtml(item.error)}` : ''}</li>`).join('')
      : '<li>暂无关联 AI 调用</li>';
    return `<div class="job-monitor-detail">
      <p>操作用户：${escapeHtml(job.createdByName || '—')}</p>
      <p>阶段：${escapeHtml(job.phase || '—')}${job.visionPhase ? '（含附件视觉识别）' : ''}</p>
      <p>附件 ${job.attachmentCount} 个${job.error ? ` · 错误：${escapeHtml(job.error)}` : ''}</p>
      <p><a href="/admin/tasks">打开任务管理</a> · <a href="/admin/files">打开文件管理</a></p>
      <ul>${callRows}</ul>
    </div>`;
  };

  const rowHtml = (job: WorkJob, open: boolean) => {
    const actions: string[] = [];
    if (job.canRetry) actions.push(`<button type="button" class="secondary-button job-retry" data-id="${escapeHtml(job.id)}">重试</button>`);
    if (job.canTimeout && canTimeout) actions.push(`<button type="button" class="danger-button job-timeout" data-id="${escapeHtml(job.id)}">结束审核</button>`);
    actions.push(`<button type="button" class="secondary-button job-toggle" data-id="${escapeHtml(job.id)}" data-family="${escapeHtml(job.family)}">${open ? '收起' : '详情'}</button>`);
    actions.push(`<button type="button" class="danger-button job-delete" data-id="${escapeHtml(job.id)}" data-family="${escapeHtml(job.family)}">删除</button>`);
    const checked = selected.has(job.id) ? ' checked' : '';
    const progress = Math.max(0, Math.min(100, Number(job.progress) || 0));
    return `<tr class="${job.stuck ? 'is-stuck' : ''}" data-job-id="${escapeHtml(job.id)}" data-family="${escapeHtml(job.family)}">
      <td><input class="job-checkbox" type="checkbox" value="${escapeHtml(job.id)}" data-family="${escapeHtml(job.family)}" aria-label="选择作业"${checked}></td>
      <td><span class="admin-job-type">${escapeHtml(job.typeLabel)}</span></td>
      <td><b class="admin-job-task">${escapeHtml(job.taskName)}</b><small class="admin-job-object">${escapeHtml(job.objectName || '—')}</small></td>
      <td>${statusTag(job)}</td>
      <td><div class="admin-job-progress"><span><i style="width:${progress}%"></i></span><em>${progress}%</em></div><small class="admin-job-phase">${escapeHtml(job.phase || '—')}</small></td>
      <td class="admin-job-duration">${escapeHtml(formatDuration(job.durationMs))}</td>
      <td>${escapeHtml(job.createdByName || '—')}</td>
      <td class="admin-job-time">${date(job.createdAt)}</td>
      <td><div class="job-actions">${actions.join('')}</div></td>
    </tr>
    <tr class="job-monitor-expand" data-expand-id="${escapeHtml(job.id)}" ${open ? '' : 'hidden'}><td colspan="9">${open ? detailHtml(job) : ''}</td></tr>`;
  };

  const renderSummary = (summary: JobsPayload['summary'], total: number) => {
    const running = host.querySelector('#jobMetricRunning');
    const stuck = host.querySelector('#jobMetricStuck');
    const failed = host.querySelector('#jobMetricFailed');
    const done = host.querySelector('#jobMetricDone');
    const note = host.querySelector('#jobsSummary');
    if (running) running.textContent = String(summary.running);
    if (stuck) stuck.textContent = String(summary.stuck);
    if (failed) failed.textContent = String(summary.failed24h);
    if (done) done.textContent = String(summary.completedToday);
    if (note) note.textContent = `当前列表 ${total} 条 · 默认优先异常作业`;
  };

  const refreshSelection = () => {
    const boxes = [...host.querySelectorAll<HTMLInputElement>('.job-checkbox')];
    const selectAll = host.querySelector<HTMLInputElement>('#jobsSelectAll');
    const summary = host.querySelector('#jobsSelectionSummary');
    const button = host.querySelector<HTMLButtonElement>('#jobsDeleteSelected');
    if (summary) summary.textContent = `已选 ${selected.size} 条`;
    if (button) button.disabled = selected.size === 0;
    if (selectAll) {
      selectAll.checked = boxes.length > 0 && boxes.every((item) => item.checked);
      selectAll.indeterminate = boxes.some((item) => item.checked) && !selectAll.checked;
    }
  };

  const deleteJobs = async (items: Array<{family: string; id: string}>) => {
    const targets = items.filter((item) => item.id && item.family);
    if (!targets.length) return;
    if (!window.confirm(`确定删除选中的 ${targets.length} 条作业记录吗？仅从作业监控中移除，不会删除客户文件与审核正文。`)) return;
    await api.post(apiUrl('/api/admin/jobs/delete'), {items: targets});
    targets.forEach((item) => {
      selected.delete(item.id);
      expanded.delete(item.id);
    });
    await load();
  };

  const bindRowActions = () => {
    host.querySelectorAll<HTMLInputElement>('.job-checkbox').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const family = checkbox.dataset.family as WorkJob['family'];
        if (checkbox.checked) selected.set(checkbox.value, family);
        else selected.delete(checkbox.value);
        refreshSelection();
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.job-toggle').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = button.dataset.id || '';
        const family = button.dataset.family || '';
        const expand = host.querySelector<HTMLTableRowElement>(`tr[data-expand-id="${CSS.escape(id)}"]`);
        if (!id || !expand) return;
        if (expanded.has(id)) {
          expanded.delete(id);
          expand.hidden = true;
          button.textContent = '详情';
          return;
        }
        expanded.add(id);
        expand.hidden = false;
        button.textContent = '收起';
        expand.querySelector('td')!.innerHTML = '正在加载详情…';
        try {
          const result = await api.get<{job: JobDetail}>(apiUrl(`/api/admin/jobs/${encodeURIComponent(family)}/${encodeURIComponent(id)}`));
          expand.querySelector('td')!.innerHTML = detailHtml(result.job, result.job);
        } catch (error) {
          expand.querySelector('td')!.textContent = error instanceof Error ? error.message : String(error);
        }
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.job-retry').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = button.dataset.id || '';
        if (!id) return;
        button.disabled = true;
        try {
          await api.post(apiUrl(`/api/admin/recognition-jobs/${encodeURIComponent(id)}/retry`), {});
          await load();
        } catch (error) {
          button.disabled = false;
          window.alert(error instanceof Error ? error.message : String(error));
        }
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.job-timeout').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = button.dataset.id || '';
        if (!id || !window.confirm('确定将该卡住的审核标记为超时结束吗？不会删除客户文件与历史结果，客户可以重新审核。')) return;
        button.disabled = true;
        try {
          await api.post(apiUrl(`/api/admin/jobs/legal-review/${encodeURIComponent(id)}/timeout`), {});
          expanded.delete(id);
          await load();
        } catch (error) {
          button.disabled = false;
          window.alert(error instanceof Error ? error.message : String(error));
        }
      });
    });
    host.querySelectorAll<HTMLButtonElement>('.job-delete').forEach((button) => {
      button.addEventListener('click', () => {
        void deleteJobs([{family: button.dataset.family || '', id: button.dataset.id || ''}]).catch((error) => {
          window.alert(error instanceof Error ? error.message : String(error));
        });
      });
    });
  };

  const load = async (silent = false) => {
    const data = await api.get<JobsPayload>(apiUrl(`/api/admin/jobs?${collectParams().toString()}`));
    fillUserSelect(host.querySelector('#jobsUser'), data.users ?? []);
    renderSummary(data.summary, Number(data.total ?? 0));
    const pageInput = host.querySelector<HTMLInputElement>('#jobsPage');
    if (pageInput) pageInput.value = String(data.page ?? 1);
    const body = host.querySelector<HTMLTableSectionElement>('#jobsMonitorBody');
    if (body) {
      const jobs = data.jobs ?? [];
      body.innerHTML = jobs.length
        ? jobs.map((job) => rowHtml(job, expanded.has(job.id))).join('')
        : '<tr><td colspan="9">暂无作业</td></tr>';
      bindRowActions();
      for (const id of [...expanded]) {
        const row = body.querySelector<HTMLTableRowElement>(`tr[data-expand-id="${CSS.escape(id)}"]`);
        const job = jobs.find((item) => item.id === id);
        if (!row || !job) continue;
        row.hidden = false;
        void api.get<{job: JobDetail}>(apiUrl(`/api/admin/jobs/${encodeURIComponent(job.family)}/${encodeURIComponent(id)}`)).then((result) => {
          if (row.isConnected) row.querySelector('td')!.innerHTML = detailHtml(result.job, result.job);
        }).catch(() => undefined);
      }
    }
    const previous = host.querySelector<HTMLButtonElement>('#jobsPrevious');
    const next = host.querySelector<HTMLButtonElement>('#jobsNext');
    const pageLabel = host.querySelector('#jobsPageLabel');
    if (previous) previous.disabled = Number(data.page ?? 1) <= 1;
    if (next) next.disabled = Number(data.page ?? 1) >= Number(data.totalPages ?? 1);
    if (pageLabel) pageLabel.textContent = `${data.page ?? 1} / ${data.totalPages ?? 1}`;
    refreshSelection();
    if (!silent) window.clearInterval(refreshTimer);
    const hasActive = (data.jobs ?? []).some((job) => job.status === 'running' || job.status === 'queued' || job.stuck);
    window.clearInterval(refreshTimer);
    if (hasActive) refreshTimer = window.setInterval(() => { void load(true); }, 10_000);
  };

  host.innerHTML = `<section class="admin-panel recognition-jobs-panel job-monitor-panel">
    <div class="admin-list-heading"><div><h2>作业监控</h2><span id="jobsSummary">正在加载…</span></div><span class="admin-list-note">默认不铺开历史成功记录</span></div>
    <div class="admin-metric-grid job-monitor-metrics">
      <article><span>进行中</span><strong id="jobMetricRunning">—</strong></article>
      <article><span>卡住</span><strong id="jobMetricStuck">—</strong></article>
      <article><span>近 24 小时失败</span><strong id="jobMetricFailed">—</strong></article>
      <article><span>今日完成</span><strong id="jobMetricDone">—</strong></article>
    </div>
    <div class="recognition-jobs-filters">
      <label>范围<select id="jobsView"><option value="attention">异常优先</option><option value="all">全部作业</option></select></label>
      <label>业务<select id="jobsBusiness"><option value="">全部业务</option><option value="finance">财务</option><option value="legal">法务</option></select></label>
      <label>类型<select id="jobsFamily"><option value="">全部类型</option><option value="recognition">报关/发票识别</option><option value="legal-review">合同智能审核</option><option value="workflow">申报与归档</option></select></label>
      <label>状态<select id="jobsStatus"><option value="">默认</option><option value="stuck">卡住</option><option value="failed">失败</option><option value="running">进行中</option><option value="completed">完成</option></select></label>
      <label>操作用户<select id="jobsUser"><option value="">全部操作用户</option></select></label>
      <label>开始日期<input id="jobsFrom" type="date"></label>
      <label>结束日期<input id="jobsTo" type="date"></label>
      <button class="primary-button" id="jobsFilter" type="button">筛选</button>
      <button class="secondary-button" id="jobsReset" type="button">重置</button>
    </div>
    <div class="admin-space-file-toolbar job-monitor-toolbar">
      <label><input id="jobsSelectAll" type="checkbox"> 全选本页</label>
      <span id="jobsSelectionSummary">已选 0 条</span>
      <button class="danger-button" id="jobsDeleteSelected" type="button" disabled>删除选中</button>
    </div>
    <input id="jobsPage" type="hidden" value="1">
    <div class="table-scroll"><table><thead><tr><th>选择</th><th>类型</th><th>任务 / 对象</th><th>状态</th><th>进度</th><th>耗时</th><th>操作用户</th><th>开始时间</th><th>操作</th></tr></thead><tbody id="jobsMonitorBody"><tr><td colspan="9">正在加载…</td></tr></tbody></table></div>
    <div class="admin-pagination"><button class="secondary-button" id="jobsPrevious" type="button">上一页</button><span id="jobsPageLabel">1 / 1</span><button class="secondary-button" id="jobsNext" type="button">下一页</button></div>
  </section>`;

  host.querySelector('#jobsFilter')?.addEventListener('click', () => {
    const page = host.querySelector<HTMLInputElement>('#jobsPage');
    if (page) page.value = '1';
    void load();
  });
  host.querySelector('#jobsReset')?.addEventListener('click', () => {
    host.querySelectorAll<HTMLInputElement>('input[type=date]').forEach((input) => { input.value = ''; });
    host.querySelectorAll<HTMLSelectElement>('select').forEach((select) => { select.value = select.id === 'jobsView' ? 'attention' : ''; });
    const page = host.querySelector<HTMLInputElement>('#jobsPage');
    if (page) page.value = '1';
    void load();
  });
  host.querySelector('#jobsPrevious')?.addEventListener('click', () => {
    const page = host.querySelector<HTMLInputElement>('#jobsPage');
    if (page) page.value = String(Math.max(1, Number(page.value) - 1));
    void load();
  });
  host.querySelector('#jobsNext')?.addEventListener('click', () => {
    const page = host.querySelector<HTMLInputElement>('#jobsPage');
    if (page) page.value = String(Number(page.value) + 1);
    void load();
  });
  host.querySelector<HTMLInputElement>('#jobsSelectAll')?.addEventListener('change', (event) => {
    const checked = (event.currentTarget as HTMLInputElement).checked;
    host.querySelectorAll<HTMLInputElement>('.job-checkbox').forEach((checkbox) => {
      checkbox.checked = checked;
      const family = checkbox.dataset.family as WorkJob['family'];
      if (checked) selected.set(checkbox.value, family);
      else selected.delete(checkbox.value);
    });
    refreshSelection();
  });
  host.querySelector('#jobsDeleteSelected')?.addEventListener('click', () => {
    void deleteJobs([...selected.entries()].map(([id, family]) => ({id, family}))).catch((error) => {
      window.alert(error instanceof Error ? error.message : String(error));
    });
  });
  await load();
}
